import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { ForcePasswordChangeDialog } from "@/components/auth/ForcePasswordChangeDialog";
import { AnalyticsProvider } from "@/components/analytics/AnalyticsProvider";
import { CookieConsentBanner } from "@/components/analytics/CookieConsentBanner";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy-loaded pages
const PublicEventPage = lazy(() => import("./pages/PublicEventPage"));
const SignUpPage = lazy(() => import("./pages/SignUpPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ChooseRolePage = lazy(() => import("./pages/ChooseRolePage"));
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const ProducerDashboardPage = lazy(() => import("./pages/ProducerDashboardPage"));
const ProducerPageSettings = lazy(() => import("./pages/ProducerPageSettings"));
const PublicProducerPage = lazy(() => import("./pages/PublicProducerPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const CreateEventPage = lazy(() => import("./pages/CreateEventPage"));
const EditEventPage = lazy(() => import("./pages/EditEventPage"));
const EventDashboardPage = lazy(() => import("./pages/EventDashboardPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const FinanceiroPage = lazy(() => import("./pages/FinanceiroPage"));
const AjudaPage = lazy(() => import("./pages/AjudaPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CancelamentoPoliticaPage = lazy(() => import("./pages/CancelamentoPoliticaPage"));
const ReembolsosPage = lazy(() => import("./pages/ReembolsosPage"));
const TrocaTitularidadePage = lazy(() => import("./pages/TrocaTitularidadePage"));
const PurchaseStatusPage = lazy(() => import("./pages/PurchaseStatusPage"));
const WaitingPaymentPage = lazy(() => import("./pages/WaitingPaymentPage"));
const TaxasPage = lazy(() => import("./pages/TaxasPage"));
const PrecosPage = lazy(() => import("./pages/PrecosPage"));
const ContatoPage = lazy(() => import("./pages/ContatoPage"));
const CheckoutPage = lazy(() => import("./pages/CheckoutPage"));
const PublicEventsPage = lazy(() => import("./pages/PublicEventsPage"));
const MyTicketsPage = lazy(() => import("./pages/MyTicketsPage"));
const PurchaseDetailPage = lazy(() => import("./pages/PurchaseDetailPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/admin/AdminAnalyticsPage"));
const AdminBirthdayReportPage = lazy(() => import("./pages/admin/AdminBirthdayReportPage"));
const AdminResendTicketsPage = lazy(() => import("./pages/admin/AdminResendTicketsPage"));
const AdminEmailHistoryPage = lazy(() => import("./pages/admin/AdminEmailHistoryPage"));
const AdminTestEmailPage = lazy(() => import("./pages/admin/AdminTestEmailPage"));
const CheckinHelpPage = lazy(() => import("./pages/CheckinHelpPage"));
const CortesiasHelpPage = lazy(() => import("./pages/CortesiasHelpPage"));
const RecursosPage = lazy(() => import("./pages/RecursosPage"));
const CasosSucessoPage = lazy(() => import("./pages/CasosSucessoPage"));
const CentralAjudaPage = lazy(() => import("./pages/CentralAjudaPage"));
const CancelamentoPoliticaHelpPage = lazy(() => import("./pages/CancelamentoPoliticaHelpPage"));
const ProductSelectionPage = lazy(() => import("./pages/ProductSelectionPage"));
const GuestTicketsHelpPage = lazy(() => import("./pages/GuestTicketsHelpPage"));

// Event management pages
const EventAnalyticsPage = lazy(() => import("./pages/event-management/EventAnalyticsPage"));
const TicketsOverviewPage = lazy(() => import("./pages/event-management/TicketsOverviewPage"));
const TicketMessagePage = lazy(() => import("./pages/event-management/TicketMessagePage"));
const ConfirmationEmailPage = lazy(() => import("./pages/event-management/ConfirmationEmailPage"));
const CouponsPage = lazy(() => import("./pages/event-management/CouponsPage"));
const EventParticipantsPage = lazy(() => import("./pages/event-management/EventParticipantsPage"));
const EventProductsPage = lazy(() => import("./pages/event-management/EventProductsPage"));
const EmailInvitePage = lazy(() => import("./pages/event-management/EmailInvitePage"));
const ParticipantEmailPage = lazy(() => import("./pages/event-management/ParticipantEmailPage"));
const ParticipantIndividualEmailPage = lazy(() => import("./pages/event-management/ParticipantIndividualEmailPage"));
const GuestAccessPage = lazy(() => import("./pages/event-management/GuestAccessPage"));
const CheckinOverviewPage = lazy(() => import("./pages/event-management/CheckinOverviewPage"));
const CheckinScannerPage = lazy(() => import("./pages/event-management/CheckinScannerPage"));
const CheckinOnlinePage = lazy(() => import("./pages/event-management/CheckinOnlinePage"));
const CheckinEtiquetasPage = lazy(() => import("./pages/event-management/CheckinEtiquetasPage"));
const AdditionalSettingsPage = lazy(() => import("./pages/event-management/AdditionalSettingsPage"));
const SeatMapPage = lazy(() => import("./pages/event-management/SeatMapPage"));
const IntegrationsPage = lazy(() => import("./pages/event-management/IntegrationsPage"));
const TransferRequestsPage = lazy(() => import("./pages/event-management/TransferRequestsPage"));
const EventFinanceiroPage = lazy(() => import("./pages/event-management/EventFinanceiroPage"));
const EventTransactionsPage = lazy(() => import("./pages/event-management/EventTransactionsPage"));
const SeatSelectionPage = lazy(() => import("./pages/SeatSelectionPage"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <ForcePasswordChangeDialog />
          <AnalyticsProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/~oauth" element={null} />
              <Route path="/" element={<Index />} />
              <Route path="/eventos" element={<PublicEventsPage />} />
              <Route path="/evento/:sequentialId" element={<PublicEventPage />} />
              <Route path="/:producerSlug/:eventSlug/:sequentialId" element={<PublicEventPage />} />
              <Route path="/:producerSlug/:eventSlug/:sequentialId/checkout" element={<CheckoutPage />} />
              <Route path="/:producerSlug/:eventSlug/:sequentialId/produtos" element={<ProductSelectionPage />} />
              <Route path="/evento/:sequentialId/checkout" element={<CheckoutPage />} />
              <Route path="/evento/:sequentialId/produtos" element={<ProductSelectionPage />} />
              <Route path="/:producerSlug/:eventSlug/:sequentialId/assentos" element={<SeatSelectionPage />} />
              <Route path="/evento/:sequentialId/assentos" element={<SeatSelectionPage />} />
              <Route path="/compra/sucesso" element={<PurchaseStatusPage status="sucesso" />} />
              <Route path="/compra/erro" element={<PurchaseStatusPage status="erro" />} />
              <Route path="/compra/pendente" element={<PurchaseStatusPage status="pendente" />} />
              <Route path="/compra/aguardando/:purchaseId" element={<WaitingPaymentPage />} />
              <Route path="/criar-conta" element={<SignUpPage />} />
              <Route path="/entrar" element={<LoginPage />} />
              <Route path="/esqueci-senha" element={<ForgotPasswordPage />} />
              <Route path="/redefinir-senha" element={<ResetPasswordPage />} />
              <Route path="/verificar-email" element={<VerifyEmailPage />} />
              <Route path="/escolher-perfil" element={<ChooseRolePage />} />
              <Route path="/como-funciona" element={<HowItWorksPage />} />

              {/* Legal pages */}
              <Route path="/termos" element={<TermsPage />} />
              <Route path="/privacidade" element={<PrivacyPage />} />
              <Route path="/politica-cancelamento" element={<CancelamentoPoliticaPage />} />
              <Route path="/reembolsos" element={<ReembolsosPage />} />
              <Route path="/troca-titularidade" element={<TrocaTitularidadePage />} />
              <Route path="/taxas" element={<TaxasPage />} />
              <Route path="/precos" element={<PrecosPage />} />
              <Route path="/contato" element={<ContatoPage />} />
              <Route path="/ajuda/checkin" element={<CheckinHelpPage />} />
              <Route path="/ajuda/cortesias" element={<CortesiasHelpPage />} />
              <Route path="/ajuda/cancelamento" element={<CancelamentoPoliticaHelpPage />} />
              <Route path="/ajuda" element={<CentralAjudaPage />} />
              <Route path="/recursos" element={<RecursosPage />} />
              <Route path="/casos" element={<CasosSucessoPage />} />

              {/* Dashboard - Producer area */}
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/dashboard/dados" element={<ProducerDashboardPage />} />
              <Route path="/dashboard/sua-pagina" element={<ProducerPageSettings />} />
              <Route path="/dashboard/perfil" element={<ProfilePage />} />
              <Route path="/dashboard/eventos" element={<EventsPage />} />
              <Route path="/dashboard/eventos/criar" element={<CreateEventPage />} />
              <Route path="/dashboard/eventos/:eventId/editar" element={<EditEventPage />} />
              <Route path="/dashboard/financeiro" element={<FinanceiroPage />} />
              <Route path="/dashboard/ajuda" element={<AjudaPage />} />
              <Route path="/dashboard/ajuda/ingressos-convidados" element={<GuestTicketsHelpPage />} />
              <Route path="/dashboard/meus-ingressos" element={<MyTicketsPage />} />
              <Route path="/dashboard/meus-ingressos/:purchaseId" element={<PurchaseDetailPage />} />

              {/* Event Management */}
              <Route path="/dashboard/eventos/:eventId" element={<EventDashboardPage />} />
              <Route path="/dashboard/eventos/:eventId/analytics" element={<EventAnalyticsPage />} />
              <Route path="/dashboard/eventos/:eventId/ingressos" element={<TicketsOverviewPage />} />
              <Route path="/dashboard/eventos/:eventId/ingressos/mensagem" element={<TicketMessagePage />} />
              <Route path="/dashboard/eventos/:eventId/ingressos/email-confirmacao" element={<ConfirmationEmailPage />} />
              <Route path="/dashboard/eventos/:eventId/ingressos/cupons" element={<CouponsPage />} />
              <Route path="/dashboard/eventos/:eventId/produtos" element={<EventProductsPage />} />
              <Route path="/dashboard/eventos/:eventId/divulgue/convite" element={<EmailInvitePage />} />
              <Route path="/dashboard/eventos/:eventId/divulgue/convidados" element={<GuestAccessPage />} />
              
              <Route path="/dashboard/eventos/:eventId/participantes" element={<EventParticipantsPage />} />
              <Route path="/dashboard/eventos/:eventId/participantes/email" element={<ParticipantEmailPage />} />
              <Route path="/dashboard/eventos/:eventId/participantes/:purchaseId/email" element={<ParticipantIndividualEmailPage />} />
              <Route path="/dashboard/eventos/:eventId/checkin" element={<CheckinOverviewPage />} />
              <Route path="/dashboard/eventos/:eventId/checkin/online" element={<CheckinOnlinePage />} />
              <Route path="/dashboard/eventos/:eventId/checkin/scanner" element={<CheckinScannerPage />} />
              <Route path="/dashboard/eventos/:eventId/checkin/etiquetas" element={<CheckinEtiquetasPage />} />
              <Route path="/dashboard/eventos/:eventId/financeiro" element={<EventFinanceiroPage />} />
              <Route path="/dashboard/eventos/:eventId/financeiro/historico" element={<EventTransactionsPage />} />
              <Route path="/dashboard/eventos/:eventId/ajuda" element={<CheckinHelpPage />} />
              <Route path="/dashboard/eventos/:eventId/configuracoes" element={<AdditionalSettingsPage />} />
              <Route path="/dashboard/eventos/:eventId/integracoes" element={<IntegrationsPage />} />
              <Route path="/dashboard/eventos/:eventId/transferencias" element={<TransferRequestsPage />} />
              <Route path="/dashboard/eventos/:eventId/mapa-assentos" element={<SeatMapPage />} />

              {/* Admin */}
              <Route path="/admin" element={<AdminDashboardPage />} />
              <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
              <Route path="/admin/aniversariantes" element={<AdminBirthdayReportPage />} />
              <Route path="/admin/reenvio-copias" element={<AdminResendTicketsPage />} />
              <Route path="/admin/emails-historico" element={<AdminEmailHistoryPage />} />
              <Route path="/admin/teste-email" element={<AdminTestEmailPage />} />

              {/* Public producer page */}
              <Route path="/:slug" element={<PublicProducerPage />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsentBanner />
          </AnalyticsProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
